import { useCallback, useMemo } from 'react';

import { MEASURE_ID_KEY } from '@portal/common/models/data-key';
import { DataProjectId } from '@portal/common/models/data-project';
import { localizeFilterValues } from '@portal/common/utility/chart-data-helpers';

import config from '../../../config';
import _ from '../../../locale';

const RESTRICTED_END_AGES_FOR_ZERO_START = [0, 1, 2, 3];
const RESTRICTED_START_AGES = [1, 2];

const RESTRICTED_MEASURES = [config.prevalenceMeasureId, config.incidenceMeasureId];

type Params = {
    dataProjectId?: number;
    selectedMeasures: number[];
    isSingleAgesReport?: boolean;
};

/**
 * Race & Ethnicity prevalence/incidence: some custom age start/end options are not modeled
 * and must be disabled with the same explanatory copy in tooltips and inline hints.
 * Restrictions only apply to aggregated reports, not single age reports.
 */
export const useRaceEthnicityAgeOptionsRestriction = ({
    dataProjectId,
    selectedMeasures,
    isSingleAgesReport,
}: Params) => {
    const hasRestrictedAgeRanges = useMemo(() => {
        if (isSingleAgesReport) {
            return false;
        }

        const isRaceEthnicity = dataProjectId === DataProjectId.RACE_ETHNICITY;
        const hasRestrictedMeasures = selectedMeasures.some((id) =>
            RESTRICTED_MEASURES.includes(id)
        );

        return isRaceEthnicity && hasRestrictedMeasures;
    }, [dataProjectId, selectedMeasures, isSingleAgesReport]);

    const restrictedMeasureIdsForLabel = useMemo(
        () => RESTRICTED_MEASURES.filter((id) => selectedMeasures.includes(id)),
        [selectedMeasures]
    );

    const restrictedMeasuresLabel = useMemo(() => {
        if (!hasRestrictedAgeRanges) {
            return '';
        }
        return localizeFilterValues(MEASURE_ID_KEY, restrictedMeasureIdsForLabel);
    }, [hasRestrictedAgeRanges, restrictedMeasureIdsForLabel]);

    const restrictedAgeRangesMessage = useMemo(
        () =>
            hasRestrictedAgeRanges
                ? _('export_single_age_restricted_range_tooltip', {
                      measures: restrictedMeasuresLabel,
                  })
                : '',
        [hasRestrictedAgeRanges, restrictedMeasuresLabel]
    );

    const isRestrictedStartAge = useCallback(
        (age: number) => hasRestrictedAgeRanges && RESTRICTED_START_AGES.includes(age),
        [hasRestrictedAgeRanges]
    );

    const isRestrictedEndAge = useCallback(
        (age: number | string, currentStart: number) =>
            hasRestrictedAgeRanges &&
            currentStart === 0 &&
            typeof age === 'number' &&
            RESTRICTED_END_AGES_FOR_ZERO_START.includes(age),
        [hasRestrictedAgeRanges]
    );

    const isRestrictedBand = useCallback(
        (startValue: number, endValue: number | string) => {
            if (!hasRestrictedAgeRanges) {
                return false;
            }
            if (RESTRICTED_START_AGES.includes(startValue)) {
                return true;
            }
            if (
                startValue === 0 &&
                typeof endValue === 'number' &&
                RESTRICTED_END_AGES_FOR_ZERO_START.includes(endValue)
            ) {
                return true;
            }
            return false;
        },
        [hasRestrictedAgeRanges]
    );

    return {
        hasRestrictedAgeRanges,
        restrictedAgeRangesMessage,
        isRestrictedStartAge,
        isRestrictedEndAge,
        isRestrictedBand,
    };
};
